<template>
	<template v-if="data">
		<section>
			<ButtonBack />

			<el-row class="titlerow">
				<el-col :sm="12">
					<h1>{{ data.title }}</h1>
				</el-col>
				<el-col :sm="12">
					<div :class="`has-status has-status--${$getStateColor('case', data.state)}`">
						{{ $getStateFullLabel('case', data.state, false) }}
					</div>
				</el-col>
			</el-row>
		</section>

		<template v-if="data.voteRoles && data.voteRoles.length > 0">
			<section class="no-marginbottom">
				<h2>Zugewiesene Gruppen</h2>
			</section>

			<RoleSlider :roles="data.voteRoles ? data.voteRoles : []" :showDelete="false" />
		</template>

		<section>
			<TheBox
				v-if="hasDecision(data)"
				:title="isSmallSenateDecision(data) ? 'Senatsentscheidung' : 'Endgültige Entscheidung:'"
				:id="'decision-final'"
			>
				<div :class="`has-status has-status--${$getStateColor('case', data.state)}`">
					{{ $getStateFullLabel('case', data.state, false) }}
				</div>

				<h3>Begründung</h3>
				<div class="text">{{ data.reasonText ? data.reasonText : '.' }}</div>

				<el-row v-if="data.decidedDate" class="decided-info">
					<el-text>Entscheidung getroffen: {{ $formatDate(data.decidedDate, 'HH:MM, DD.MM.YYYY') }}</el-text>
				</el-row>
			</TheBox>
		</section>

		<SectionStatistics
			v-if="showSmallSenateStatistic(data)"
			:statistics="statistic"
			:type="DecisionType.SmallSenate"
			:title="data.preVoteRole?.name"
			:werberatRoles="null"
			:caseHid="data.hid"
		/>
		<SectionStatistics
			v-else-if="showWerberatStatistic(data)"
			:statistics="statistic"
			:type="DecisionType.Werberat"
			:werberatRoles="data.voteRoles"
			:caseHid="data.hid"
		/>

		<SectionBeschwerden :complaints="data.complaints ? data.complaints : []" @loadNextPage="loadNextPage" />

		<SectionStatements v-if="data.statements && data.statements.length > 0" :statements="data.statements ? data.statements : []" />

		<DetailsMedia
			v-if="caseFiles.length > 0"
			:files="caseFiles"
			@addToFiles="null"
			@removeFromFiles="removeFromFiles"
			:showAddButton="false"
			:pageType="'fall'"
		/>

		<DetailsLinks
			v-if="caseLinks.length > 0"
			:links="caseLinks"
			@addToLinks="null"
			@removeFromLinks="removeFromLinks"
			:showAddButton="false"
			:pageType="'fall'"
		/>

		<section>
			<TheBox :title="'Hinweis'" v-if="data.externalNote">
				<div class="text">{{ data.externalNote }}</div>
			</TheBox>

			<TheBox :title="'Fall Infos'" :hasDataGrid="true">
				<el-row>
					<el-col :sm="12" :xs="24">
						<el-form-item label="Falltitel" prop="title">
							<span>{{ data.title }}</span>
						</el-form-item>

						<el-form-item label="Beschwerdegrund:" prop="category">
							<span>{{ data?.category.name }}</span>
						</el-form-item>

						<el-form-item label="Medium:" prop="mediums">
							<span>{{ data?.mediums.map((medium) => medium.name).join(', ') }}</span>
						</el-form-item>

						<el-form-item label="Reichweite:" prop="mediums">
							<span>{{ data?.reaches.length > 0 ? data?.reaches.map((medium) => medium.name).join(', ') : '-' }}</span>
						</el-form-item>

						<el-form-item label="Datum:">
							<span>{{ $formatDate(data.createdDate) }}</span>
						</el-form-item>

						<el-form-item label="ID:">
							<span>{{ data.hid }}</span>
						</el-form-item>

						<el-form-item label="Rückfragen:">
							<span>{{ getCallbacksCount(data) }}</span>
						</el-form-item>
					</el-col>

					<el-col :sm="12" :xs="24">
						<el-form-item label="Beschwerden:">
							<span>{{ data.complaints?.length }}</span>
						</el-form-item>

						<el-form-item label="Stn-Gruppen:">
							{{ data.voteRoles && data.voteRoles.length > 0 ? data.voteRoles?.map((role) => role.name).join(', ') : '-' }}
						</el-form-item>

						<el-form-item label="Stn. bis:">
							<span>{{ getCaseVoteEnde(data, $formatDate) }}</span>
						</el-form-item>

						<el-form-item label="Stn. angefragt:">
							<span>{{ data.statements?.length }}</span>
						</el-form-item>

						<el-form-item label="Stn. angefragt:">
							<span>{{ data.statements?.filter((st) => st.answer).length }}</span>
						</el-form-item>

						<el-form-item label="WR-Ent. bis:">
							<span>{{ data.voteEndDate ? $formatDate(data.voteEndDate) : '-' }}</span>
						</el-form-item>

						<el-form-item label="Auf Website veröffentlicht:">
							<span>{{ data.publishedDate ? 'Ja' : 'Nein' }}</span>
						</el-form-item>
					</el-col>
				</el-row>
			</TheBox>
		</section>
	</template>
	<section v-else-if="loading === false">
		<ButtonBack />
		<el-alert :title="texts.general.emptyText" type="error" effect="dark" :closable="false" />
	</section>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import BoxSenateDecision from '@/components/boxes/BoxSenateDecision.vue';
import BoxWerberatDecision from '@/components/boxes/BoxWerberatDecision.vue';
import DialogPreliminaryDecision from '@/components/dialogs/DialogPreliminaryDecision.vue';
import DialogPublishCase from '@/components/dialogs/DialogPublishCase.vue';
import DialogRequestStatement from '@/components/dialogs/DialogRequestStatement.vue';
import DialogSendCaseSmallSenate from '@/components/dialogs/DialogSendCaseSmallSenate.vue';
import ComplaintsTable from '@/components/tables/ComplaintsTable.vue';
import LogsTable from '@/components/tables/LogsTable.vue';
import StatementsTable from '@/components/tables/StatementsTable.vue';
import { getCase, getCaseLogs, getCaseStatistics } from '@/requests/cases';
import { SaveCaseRequest } from '@/types/formdataTypes';
import { getCaseFiles } from '@/utils/fileHandling/files';
import { getCaseLinks } from '@/utils/linkHandling/links';
import { startLoading, stopLoading } from '@/utils/loading/loadingHandler';
import { Delete, Plus } from '@element-plus/icons-vue';

import saveIcon from '../assets/icons/saveIcon.vue';
import ButtonBack from '../components/buttons/ButtonBack.vue';
import ButtonSticky from '../components/buttons/ButtonSticky.vue';
import RoleSlider from '../components/slider/RoleSlider.vue';
import DecisionsTable from '../components/tables/DecisionsTable.vue';
import TheBox from '../components/TheBox.vue';
import TheButton from '../components/TheButton.vue';
import TheForm from '../components/TheForm.vue';
import { HistoryEntryResponse, CaseResponse, CaseState, DetailFile, DetailLink, SendCaseType, DecisionType, StatisticResponse } from '../types/types';
import { texts } from '../utils/texts.js';
import DetailsLinks from './sections/DetailsLinks.vue';
import DetailsMedia from './sections/DetailsMedia.vue';

import SectionDecision from './sections/SectionDecision.vue';
import SectionBeschwerden from './sections/SectionBeschwerden.vue';
import SectionStatements from './sections/SectionStatements.vue';
import SectionStatistics from './sections/SectionStatistics.vue';

import {
	getCallbacksCount,
	getCaseVoteEnde,
	isSmallSenateDecision,
	hasDecision,
	isSentToSmallSenate,
	isInStatements,
	isSentToWerberat,
} from '@/utils/casesHandling/cases';

import { showSmallSenateStatistic, showWerberatStatistic } from '@/utils/statisticHandling/statistics';

export default defineComponent({
	name: 'FallDetailVorstand',
	data() {
		return {
			data: null as CaseResponse | null,
			saveCaseRequest: null as SaveCaseRequest | null,
			loading: true,
			SendCaseType,
			CaseState,
			caseFiles: [] as DetailFile[],
			caseLinks: [] as DetailLink[],
			logs: [] as HistoryEntryResponse[],
			logsLoading: true,
			statistic: null as StatisticResponse | null,
			DecisionType,
		};
	},
	props: {
		id: {
			type: String,
			required: true,
		},
	},
	components: {
		ButtonBack,
		ButtonSticky,
		TheButton,
		TheForm,
		TheBox,
		DecisionsTable,
		Plus,
		Delete,
		RoleSlider,
		DetailsMedia,
		DetailsLinks,
		saveIcon,
		ComplaintsTable,
		StatementsTable,
		BoxSenateDecision,
		BoxWerberatDecision,
		DialogPreliminaryDecision,
		DialogSendCaseSmallSenate,
		DialogRequestStatement,
		DialogPublishCase,
		LogsTable,
		SectionDecision,
		SectionBeschwerden,
		SectionStatements,
		SectionStatistics,
	},
	computed: {
		texts() {
			return texts;
		},
	},
	methods: {
		loadNextPage() {},
		async loadCase() {
			const loading = startLoading('#fall');

			const result = await getCase(this.id);
			if (result.success) {
				this.data = result.data;
				this.caseFiles = getCaseFiles(this.data);
				this.caseLinks = getCaseLinks(this.data);

				this.loadLogs();
			}

			this.loading = false;
			stopLoading(loading);
		},
		async loadLogs() {
			this.logsLoading = true;
			const result = await getCaseLogs(this.id);
			if (result.success) {
				this.logs = result.data;
			}
			this.logsLoading = false;
		},
		removeFromFiles(file) {
			this.caseFiles = this.caseFiles.filter((f) => f.file !== file.file);
		},
		removeFromLinks(link) {
			this.caseLinks = this.caseLinks.filter((l) => l.link !== link.link);
		},
		async loadStatistics() {
			const result = await getCaseStatistics(this.id);
			if (result.success) {
				this.statistic = result.data;
			}
		},
		getCallbacksCount,
		getCaseVoteEnde,
		isSmallSenateDecision,
		hasDecision,
		isSentToSmallSenate,
		isInStatements,
		isSentToWerberat,
		showSmallSenateStatistic,
		showWerberatStatistic,
	},
	mounted() {
		this.loadCase();
		this.loadStatistics();
	},
});
</script>
